import { Button } from "@/components/ui/button";
import { FilePlus2, X } from "lucide-react";

// "YOU LEFT 4 CHILD PARTS UNFINISHED" — his ask, 2026-10-16, small and riding with this batch.
//
// The work itself is already safe: `useDraft` keeps every half-written child in sessionStorage and
// hands it back when the dialog reopens. What was missing is that NOBODY COULD SEE IT. You had to
// remember to reopen the same dialog on the same part, and the one thing somebody interrupted mid-
// job does not have is memory. So the part says it out loud, at the top, where he is already looking.
//
// Reads sessionStorage only — no endpoint, no write, nothing in anybody else's counts, because a
// half-written child part is not a part.
const KINDS = [
  { key: "split", label: "child part", open: "split", count: (d) => (d.children || []).filter((c) => (c.tag_code || "").trim() || (c.name || "").trim()).length },
  { key: "bulkspawn", label: "copy row", open: "bulkspawn", count: (d) => (d.rows || []).filter((r) => (r.variant_label || "").trim() || (parseInt(r.count, 10) || 0) > 1).length },
  { key: "spinoff", label: "spin-off", open: "spinoff", count: (d) => (((d.tag || "").trim() || (d.label || "").trim() || (d.note || "").trim()) ? 1 : 0) },
];

export function readDrafts(partId) {
  if (typeof window === "undefined" || !partId) return [];
  const out = [];
  for (const k of KINDS) {
    try {
      const raw = window.sessionStorage.getItem(`fas.draft.${k.key}:${partId}`);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const n = k.count(parsed || {});
      if (n > 0) out.push({ ...k, n, at: parsed?._at });
    } catch { /* a blocked or corrupt store must never break the page */ }
  }
  return out;
}

export function clearDrafts(partId) {
  for (const k of KINDS) {
    try { window.sessionStorage.removeItem(`fas.draft.${k.key}:${partId}`); } catch { /* ignore */ }
  }
}

export function DraftRecoveryBanner({ partId, drafts, onResume, onDiscard }) {
  if (!drafts?.length) return null;
  const words = drafts.map((d) => `${d.n} ${d.label}${d.n === 1 ? "" : "s"}`).join(" and ");
  const when = drafts.map((d) => d.at).filter(Boolean).sort().pop();
  return (
    <div data-testid="draft-recovery-banner"
      className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-center gap-3 flex-wrap">
      <FilePlus2 className="w-5 h-5 text-amber-600 shrink-0" />
      <span className="min-w-0">
        <span data-testid="draft-recovery-text" className="block text-sm font-semibold text-amber-900">
          You left {words} unfinished on this part
        </span>
        <span className="block text-[11px] text-amber-700">
          Still here on this device{when ? ` since ${when.replace("T", " ").slice(0, 16)}` : ""} — nothing has been saved to the yard yet.
        </span>
      </span>
      <div className="ml-auto flex items-center gap-1.5">
        {drafts.map((d) => (
          <Button key={d.key} data-testid={`draft-recovery-resume-${d.key}`} size="sm"
            onClick={() => onResume(d.open)}
            className="rounded-lg h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white">
            Pick up the {d.label}{d.n === 1 ? "" : "s"}
          </Button>
        ))}
        <Button data-testid="draft-recovery-discard" size="sm" variant="outline"
          onClick={() => onDiscard(partId)} className="rounded-lg h-8 text-xs text-slate-500">
          <X className="w-3.5 h-3.5 mr-1" />Discard
        </Button>
      </div>
    </div>
  );
}
